import { enterpriseDashboardData } from "../data/enterpriseDashboard";
import type { CompanyDashboardApiResponse } from "./types";

const CARE_TYPE_KEYS = [
  "pharmacy",
  "nursing",
  "imagingLab",
  "consultation",
  "surgery",
  "rehabilitation",
] as const;

/** Libellés API (CompanyDashboardModel::$careTypeLabels) → clés i18n. */
const CARE_TYPE_API_NAMES: Record<(typeof CARE_TYPE_KEYS)[number], string> = {
  pharmacy: "Pharmacie",
  nursing: "Nursing",
  imagingLab: "Imagerie, Labo et Diagnostic",
  consultation: "Consultation (générale, spécialisée)",
  surgery: "Chirurgie",
  rehabilitation: "Support et rééducation",
};

function normalizeCareTypeName(name: string): string {
  return name.trim().toLowerCase();
}

function buildCareTypeRows(
  careTypes: Array<Record<string, unknown>>,
): Array<{ type: (typeof CARE_TYPE_KEYS)[number]; quantity: number; price: string }> {
  const byName = new Map<string, Record<string, unknown>>();
  for (const row of careTypes) {
    const key = normalizeCareTypeName(String(row.name ?? ""));
    if (key) {
      byName.set(key, row);
    }
  }

  return CARE_TYPE_KEYS.map((type) => {
    const row = byName.get(normalizeCareTypeName(CARE_TYPE_API_NAMES[type])) ?? {};
    const priceValue = Number(row.price ?? 0);
    return {
      type,
      quantity: Number(row.qty ?? 0),
      price: fmtMoney(priceValue),
    };
  });
}

function fmtMoney(value: unknown): string {
  const n = Number(value ?? 0);
  return `$${n.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
}

function fmtPercent(value: unknown): string {
  const n = Number(value ?? 0);
  return `${n.toLocaleString("fr-FR", { maximumFractionDigits: 1 })} %`;
}

function fmtDateFr(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("fr-FR");
}

function padRanking(
  rows: Array<{ name?: string; cnt?: number; count?: number }> | undefined,
): Array<{ name: string; count: number }> {
  const mapped = (rows ?? []).map((row) => ({
    name: String(row.name ?? "—"),
    count: Number(row.cnt ?? row.count ?? 0),
  }));
  while (mapped.length < 5) {
    mapped.push({ name: "—", count: 0 });
  }
  return mapped.slice(0, 5);
}

export type EnterpriseDashboardViewData = typeof enterpriseDashboardData;

export function mapCompanyDashboardResponse(
  response: CompanyDashboardApiResponse,
): EnterpriseDashboardViewData {
  const d = response.data as Record<string, unknown>;
  const budget = (d.budget ?? {}) as Record<string, unknown>;
  const effectif = (d.effectif ?? {}) as Record<string, unknown>;
  const frequentation = (d.frequentation ?? {}) as Record<string, unknown>;
  const avg = (d.avg_consumption ?? {}) as Record<string, unknown>;
  const invoices = (d.invoices ?? {}) as Record<string, Record<string, unknown>>;
  const economy = (d.economy ?? {}) as Record<string, unknown>;
  const topVisits = (d.top_visits ?? {}) as Record<string, unknown>;
  const topPathologies = (d.top_pathologies ?? {}) as Record<string, unknown>;
  const careTypes = (d.care_types ?? []) as Array<Record<string, unknown>>;
  const agePyramid = (d.age_pyramid ?? []) as Array<Record<string, unknown>>;
  const convMeta = (d.convention_meta ?? {}) as Record<string, unknown>;
  const period = response.period ?? {};
  const forecastYear = String(budget.forecast_year ?? new Date().getFullYear());
  const forecastPeriodLabel = String(budget.forecast_period_label ?? "");
  const consumptionPeriodLabel = String(budget.consumption_as_of_label ?? "");
  const validUntilLabel = String(
    budget.valid_until_label ?? `Prévision annuelle ${forecastYear}`,
  );
  const allocationDetail = String(budget.allocation_detail ?? "");
  let periodStart = `01/01/${forecastYear}`;
  let periodEnd = `31/12/${forecastYear}`;
  const forecastMatch = forecastPeriodLabel.match(/Du (\d{2}\/\d{2}\/\d{4}) au (\d{2}\/\d{2}\/\d{4})/);
  if (forecastMatch) {
    [, periodStart, periodEnd] = forecastMatch;
  }
  const consumptionDateLabel =
    consumptionPeriodLabel ||
    (period.active ? fmtDateFr(period.date_end as string | null) : fmtDateFr(null));
  const agents = Number(effectif.agents ?? 0);
  const admittedAgents = Number(frequentation.agents ?? 0);

  const careTypeRows = buildCareTypeRows(careTypes);

  const admissionsValue = period.active
    ? String(d.admissions_period ?? 0)
    : String(d.admissions_today ?? 0);

  return {
    header: {
      budgetLabel: Number(budget.allocated ?? 0).toLocaleString("en-US", {
        maximumFractionDigits: 0,
      }),
      forecastYear,
      beneficiaries: Number(effectif.total ?? 0),
      companyBadge: String(d.company_name ?? "Entreprise"),
      displayDate: new Date().toLocaleDateString("fr-FR", {
        day: "numeric",
        month: "long",
        year: "numeric",
      }),
      budgetHeader: String(convMeta.budget_header ?? ""),
    },
    budget: {
      allocatedAmount: fmtMoney(budget.allocated),
      forecastLabel: validUntilLabel,
      forecastPeriodLabel: forecastPeriodLabel || `Du ${periodStart} au ${periodEnd}`,
      allocationDetail,
      periodStart,
      periodEnd,
      cumulativeDate: consumptionDateLabel,
      cumulativePercent: fmtPercent(budget.consumed_pct),
      cumulativeAmount: fmtMoney(budget.consumed),
      outOfPackageDate: consumptionDateLabel,
      outOfPackagePercent: fmtPercent(budget.consumed_hors_forfait_pct),
      outOfPackageAmount: fmtMoney(budget.consumed_hors_forfait),
    },
    averageConsumption: {
      attendanceRate: fmtPercent(avg.attendance_pct),
      amountPerAdmission: fmtMoney(avg.amount_per_admission),
      amountPerAgent: fmtMoney(avg.amount_per_agent),
      admittedAgents: `${admittedAgents} / ${agents}`,
    },
    workforce: {
      total: String(effectif.total ?? 0),
      agents: String(effectif.agents ?? 0),
      spouses: String(effectif.spouses ?? 0),
      children: String(effectif.children ?? 0),
    },
    attendance: {
      total: String(
        Number(frequentation.agents ?? 0) +
          Number(frequentation.spouses ?? 0) +
          Number(frequentation.children ?? 0),
      ),
      agents: String(frequentation.agents ?? 0),
      spouses: String(frequentation.spouses ?? 0),
      children: String(frequentation.children ?? 0),
    },
    metrics: {
      admissions: {
        value: admissionsValue,
        footer: period.active ? "Sur la période" : "Fréquentation du jour",
      },
      allInvoices: {
        value: String(invoices.all?.qty ?? 0),
        amount: fmtMoney(invoices.all?.amount),
      },
      outOfPackageInvoices: {
        value: String(invoices.out_of_plan?.qty ?? 0),
        amount: fmtMoney(invoices.out_of_plan?.amount),
      },
      inProgress: {
        value: String(invoices.pending?.qty ?? 0),
        amount: fmtMoney(invoices.pending?.amount),
      },
      validated: {
        value: String(invoices.validated?.qty ?? 0),
        amount: fmtMoney(invoices.validated?.amount),
      },
      savings: {
        value: fmtPercent(economy.rate),
        footer: fmtMoney(economy.amount),
        requested: fmtMoney(economy.requested),
        validated: fmtMoney(economy.validated),
      },
    },
    quickAccess: [...enterpriseDashboardData.quickAccess],
    visitDistribution: {
      agents: padRanking(topVisits.agents as Array<{ name?: string; cnt?: number }>),
      spouses: padRanking(topVisits.spouses as Array<{ name?: string; cnt?: number }>),
      children: padRanking(topVisits.children as Array<{ name?: string; cnt?: number }>),
    },
    frequentPathologies: {
      agents: padRanking(topPathologies.agents as Array<{ name?: string; cnt?: number }>),
      spouses: padRanking(topPathologies.spouses as Array<{ name?: string; cnt?: number }>),
      children: padRanking(topPathologies.children as Array<{ name?: string; cnt?: number }>),
    },
    careTypes: careTypeRows,
    agePyramid: (agePyramid.length > 0 ? agePyramid : enterpriseDashboardData.agePyramid).map(
      (row) => ({
        label: String(row.label ?? ""),
        value: Number(row.pct ?? row.value ?? 0),
      }),
    ),
  };
}
